import { ReactNode } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import {
  EscalationReason,
  ThreadAIOutcome,
  TriggeredBy,
  TriggerType,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ThreadAIActivity } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { emitOpenMessageInsightsPanel } from '@frontend/inbox-call-item-event-bus';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { useScopedAppFlagStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, Chat, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';

type Props = {
  aiItem: ThreadAIActivity;
};

const BUBBLE_MAP_KEYS = {
  ACTIVATION: 'activation',
  DEACTIVATION: 'deactivation',
  SCHEDULE_COMPLETE: 'schedule-complete',
  RESCHEDULE_COMPLETE: 'reschedule-complete',
  SCHEDULE_CANCELED: 'schedule-canceled',
  ASSIST_SUCCESS: 'assist-success',
  SCHEDULE_INCOMPLETE: 'schedule-incomplete',
  ASSIST_FAIL: 'assist-fail',
} as const;

type BubbleMapKey = (typeof BUBBLE_MAP_KEYS)[keyof typeof BUBBLE_MAP_KEYS];

const NO_RELATED_SMS_ID = '00000000-0000-0000-0000-000000000000';

export const AiThreadItem = ({ aiItem }: Props) => {
  const { t } = useTranslation('thread-body');
  const timestamp = new Date(aiItem.createdAt || '');
  const { navigateToThread } = useInboxNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const messageInsightsPanelFlag = getFeatureFlagValue('inbox:message-insights-panel', 'ALL');

  const { getTriggerProps, getDialogProps } = usePopoverDialog({
    placement: 'top-end',
    interactionOptions: {
      hover: { enabled: true },
      click: { enabled: false },
    },
  });

  const bubbleMap: Record<
    BubbleMapKey,
    {
      text: ReactNode;
      iconName: IconName;
      iconColor: IconColorType;
      bold: string;
      italic: string;
      needsAttention?: boolean;
      iconStyles?: Interpolation<Theme>;
    }
  > = {
    [BUBBLE_MAP_KEYS.ACTIVATION]: {
      text: t('AI Receptionist is now active'),
      iconName: 'x',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
    },
    [BUBBLE_MAP_KEYS.DEACTIVATION]: {
      text: t('AI Receptionist went inactive'),
      iconName: 'x',
      iconColor: 'default',
      bold: 'normal',
      italic: 'italic',
    },
    [BUBBLE_MAP_KEYS.SCHEDULE_COMPLETE]: {
      text: t('Patient was added to the schedule'),
      iconName: 'calendar-scheduled',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
      iconStyles: addedToScheduleStyles,
    },
    [BUBBLE_MAP_KEYS.RESCHEDULE_COMPLETE]: {
      text: t("Patient's appointment was rescheduled"),
      iconName: 'calendar-rescheduled',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
      iconStyles: rescheduledStyles,
    },
    [BUBBLE_MAP_KEYS.SCHEDULE_CANCELED]: {
      text: t("Patient's appointment was canceled"),
      iconName: 'calendar-canceled',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
      iconStyles: canceledStyles,
    },
    [BUBBLE_MAP_KEYS.ASSIST_SUCCESS]: {
      text: t('Successfully assisted this patient'),
      iconName: 'check',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
    },
    [BUBBLE_MAP_KEYS.SCHEDULE_INCOMPLETE]: {
      text: (
        <div>
          <Text as='span' weight='bold' size='small'>
            {t('Scheduling Incomplete: ')}
          </Text>
          <Text as='span' weight='regular' size='small'>
            {t('Task created & applied to ')}
          </Text>
          <Text
            as='span'
            color='primary'
            weight='regular'
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              navigateToThread({
                threadId: aiItem.threadId || '',
                groupId: aiItem.locationId || '',
                personId: undefined,
                personPhone: aiItem.personPhoneNumber || '',
                threadItemId: aiItem.relatedSmsId,
                threadItemCreatedAt: aiItem.relatedSmsCreatedAt,
                replace: true,
              });
            }}
            css={{ cursor: 'pointer' }}
          >
            {t('message')}
          </Text>
        </div>
      ),
      iconName: 'aineeds-attention',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
      needsAttention: true,
    },
    [BUBBLE_MAP_KEYS.ASSIST_FAIL]: {
      text: (
        <div>
          <Text as='span' weight='bold' size='small'>
            {t('Unable to assist this patient: ')}
          </Text>
          <Text as='span' weight='regular' size='small'>
            {t('Task created & applied to ')}
          </Text>
          <Text
            as='span'
            color='primary'
            weight='regular'
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              navigateToThread({
                threadId: aiItem.threadId || '',
                groupId: aiItem.locationId || '',
                personId: undefined,
                personPhone: aiItem.personPhoneNumber || '',
                threadItemId: aiItem.relatedSmsId,
                threadItemCreatedAt: aiItem.relatedSmsCreatedAt,
                replace: true,
              });
            }}
            css={{ cursor: 'pointer' }}
          >
            {t('message')}
          </Text>
        </div>
      ),
      iconName: 'aineeds-attention',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
      needsAttention: true,
    },
  };

  const getBubbleInfo = () => {
    if (
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ACTIVATION ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_PARTNER_AI) ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_PATIENT)
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ACTIVATION];
    }

    if (
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_DEACTIVATION ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_OUTBOUND_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_USER) ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_USER)
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.DEACTIVATION];
    }

    if (
      (aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_ASSISTANCE_COMPLETE ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_FAQ_ASSISTANCE_COMPLETE) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ASSIST_SUCCESS];
    }

    if (
      (aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_BOOKING_COMPLETION ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.SCHEDULE_COMPLETE];
    }

    if (
      aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.RESCHEDULE_COMPLETE];
    }

    if (
      aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.SCHEDULE_CANCELED];
    }

    if (
      (aiItem.escalationReason === EscalationReason.ESCALATION_REASON_SERVICE_STAFF_NOT_FOUND ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_DISALLOWED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RESCHEDULE_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CANCEL_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_NO_AVAILABILITY_FOUND ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RESCHEDULE_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CANCEL_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_FAILURE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRMATION_FAILURE) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.SCHEDULE_INCOMPLETE];
    }

    if (
      (aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CALLBACK_CUSTOMER ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CUSTOMER_UNRESPONSIVE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_LANDLINE_CALL ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_UNRESPONSIVE_VOICEMAIL ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RUNNING_LATE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_NOT_FOUND) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ASSIST_FAIL];
    }

    // this is a default catch all for now until all reasons are mapped from TL
    if (aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED) return bubbleMap[BUBBLE_MAP_KEYS.ASSIST_FAIL];

    return;
  };

  const getOutcomeBubbleNode = (): ReactNode => (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(1),
      }}
    >
      {getBubbleInfo()?.iconName !== 'x' && (
        <Icon
          name={getBubbleInfo()?.iconName ?? 'check'}
          size={16}
          color={getBubbleInfo()?.iconColor ?? 'success'}
          css={getBubbleInfo()?.needsAttention ? needsAttentionStyles : undefined}
        />
      )}
      <Text as='span'>{getBubbleInfo()?.text}</Text>
    </div>
  );

  const primaryActions = [
    {
      label: t('View Insights'),
      onClick: () => {
        emitOpenMessageInsightsPanel({
          messageId: aiItem.relatedSmsId || '',
          threadId: aiItem.threadId || '',
          locationId: aiItem.locationId || '',
          createdAt: aiItem.relatedSmsCreatedAt || '',
          escalationSubject: aiItem.escalationSubject || '',
          escalationBody: aiItem.escalationBody || '',
          outcomeBubbleNode: getOutcomeBubbleNode(),
        });
      },
      icon: 'document-filled' as IconName,
      trackingId: `${InboxPrefixes.Thread}-message-insights`,
    },
  ];

  return (
    <>
      <Chat
        direction='outbound'
        avatar={
          <div
            css={{
              height: theme.spacing(3),
              width: theme.spacing(3),
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <Icon name='aiassistant' size={16} color='subdued' />
          </div>
        }
        timestamp={timestamp}
        css={{
          padding: theme.spacing(0, 3),
        }}
      >
        <Chat.Bubble
          variant='ai-activity'
          text={getBubbleInfo()?.text}
          leadingIcon={
            getBubbleInfo()?.iconName !== 'x' && (
              <Icon
                name={getBubbleInfo()?.iconName ?? 'check'}
                size={16}
                color={getBubbleInfo()?.iconColor ?? 'success'}
                css={getBubbleInfo()?.needsAttention ? needsAttentionStyles : getBubbleInfo()?.iconStyles}
              />
            )
          }
          timestamp={timestamp}
          styles={{
            height: theme.spacing(2),
            padding: theme.spacing(0, 0, 0, 1),
            backgroundColor: theme.colors.white,
            p: {
              fontWeight: getBubbleInfo()?.bold,
              fontStyle: getBubbleInfo()?.italic,
              color: theme.colors.neutral80,
            },
            before: {
              height: 24,
              visibility: 'hidden',
            },
          }}
          {...getTriggerProps()}
        />
      </Chat>
      {messageInsightsPanelFlag && getBubbleInfo()?.iconName !== 'x' && aiItem?.relatedSmsId !== NO_RELATED_SMS_ID && (
        <PopoverDialog
          {...getDialogProps()}
          css={{
            padding: 0,
          }}
        >
          {primaryActions?.map(({ label, onClick, icon, trackingId }, index) => (
            <Button
              key={`primary-action-${trackingId || index}`}
              className='primary-actions-button'
              hoverLabel={label}
              trackingId={trackingId}
              onClick={onClick}
              iconName={icon}
              variant='secondary'
            />
          ))}
        </PopoverDialog>
      )}
    </>
  );
};

// will move to a shared widget in future pr that is currently WIP
const needsAttentionStyles = {
  borderRadius: theme.borderRadius.small,
  background: `linear-gradient(100deg, ${theme.colors.warning10} 0%, ${theme.colors.warning5} 100%)`,
  svg: {
    color: theme.colors.warning80,
  },
};

const addedToScheduleStyles = {
  color: theme.colorsV2.blue50(),
};

const rescheduledStyles = {
  color: theme.colorsV2.eggplant50(),
};

const canceledStyles = {
  color: theme.colorsV2.tangerine50(),
};
